/**
 * Callout — a tinted left-rail block used for executive summaries,
 * trajectory-check verdicts, and "what this means" narrative footers.
 *
 * Rendered from the `emit_callout` chat-only tool (PLT-4590; previously
 * a `:::callout{tone="…"} … :::` inline directive). The tool's body
 * markdown is routed through `ChatMarkdown` and lands here as
 * children, so any markdown the model writes inside (paragraphs,
 * inline `<badge>` / `<cite id="…"/>` directives, follow-up chips,
 * platform deep-links) renders normally — we only own the surrounding
 * box.
 *
 * Tone defaults to neutral. The optional `title` renders bold above the
 * body. Long bodies are not truncated — these blocks are meant to hold
 * the verdict, so cutting them off would defeat the purpose.
 */
import type { ReactNode } from 'react';
import { coerceTone, type Tone } from './tone';

export interface CalloutProps {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
}

export function Callout({ tone = 'neutral', title, children }: CalloutProps) {
  const safeTone: Tone = coerceTone(tone);
  return (
    <aside className={`callout callout--${safeTone}`} role="note">
      {title !== undefined && title !== '' && <div className="callout-title">{title}</div>}
      <div className="callout-body">{children}</div>
    </aside>
  );
}
